/**
 * 만료(TTL) 있는 sessionStorage 헬퍼 — 짧게 살아야 하는 단발성 흐름 상태를 위한 공용 봉투(envelope).
 *
 * 동기: 로그인 유도 직전 보존하는 흐름 상태(보류 담기 의도·복귀지 등)는 사용자가 로그인하지 않고 이탈하면
 * sessionStorage 에 고아로 남아, 같은 탭에서 한참 뒤 로그인·재방문 시 잊힌 동작으로 되살아날 수 있다
 * (깨진 유리창). 값에 저장 시각을 함께 묶고, 읽을 때 TTL 초과면 폐기해 이 잔존을 원천 차단한다.
 *
 * 스토리지 선택: sessionStorage(탭 단위·탭 종료 시 소멸)를 쓰되 TTL 로 탭 내 수명을 더 짧게 좁힌다.
 * 구글 SSO 는 백엔드 OAuth 왕복(브라우저 전체 리다이렉트)에서도 같은 탭이라 sessionStorage 가 생존한다.
 *
 * 중복 방지: 흐름 상태마다 TTL 봉투 로직을 store 에 재구현하지 않도록 단일 지점으로 둔다(DRY).
 * 시간 기준은 Date.now() — 클라이언트 시계 의존이나, 보안 임계가 아닌 UX 흐름 상태라 허용 가능하다
 * (보안 임계 단발성은 백엔드 핸드오프 TTL 이 별도로 보증).
 */

/** 저장 봉투 — 값 + 저장 시각(epoch ms). JSON 직렬화로 sessionStorage 에 보관한다. */
interface Envelope<T> {
  value: T;
  savedAt: number;
}

/**
 * sessionStorage 핸들 — 브라우저에서만 존재한다(SSG 빌드 타임 SSR 엔 globalThis.sessionStorage 미정의).
 * 스토어 setup 이 프리렌더 중 이 유틸을 호출해도 ReferenceError 가 나지 않도록 단일 지점에서 가드한다.
 * SSR 에선 흐름 상태 보존이 의미 없으므로 null → 호출부는 "값 없음"으로 안전 degrade 한다.
 *
 * ⚠️ getter 접근 자체를 try 로 감싼다 — 스토리지를 차단한 브라우저·opaque origin(sandbox iframe 등)에서는
 * `globalThis.sessionStorage` 를 "읽는 것만으로" SecurityError 가 던져진다. 이를 잡지 않으면 호출부의
 * setItem catch 에 닿기 전에 로그인 복귀지·보류 담기 흐름이 중단된다. 접근 불가 시 null 로 degrade.
 */
/**
 * 저장 영역 — 기본은 sessionStorage(탭 단위 흐름 상태). `local` 은 탭을 넘어 오래 남아야 하는 개인 편의
 * 상태 전용(예: 통합 검색 최근 검색어 90일 — 078). 같은 봉투·TTL 규칙을 공유해 만료 로직을 재구현하지 않는다.
 */
export type StorageArea = 'session' | 'local';

function storage(area: StorageArea = 'session'): Storage | null {
  try {
    if (typeof globalThis === 'undefined') return null;
    const target = area === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    return target ?? null;
  } catch {
    return null;
  }
}

/**
 * TTL 봉투로 값을 저장한다.
 * @param key sessionStorage 키.
 * @param value 보존할 값(JSON 직렬화 가능해야 함).
 * @param area 저장 영역(기본 session).
 */
export function setWithTtl<T>(key: string, value: T, area: StorageArea = 'session'): void {
  const store = storage(area);
  if (!store) return; // SSR — 보존 불가, 안전 무시.
  const envelope: Envelope<T> = { value, savedAt: Date.now() };
  try {
    store.setItem(key, JSON.stringify(envelope));
  } catch {
    // setItem 은 Safari Private 모드·용량 초과·스토리지 차단 시 throw 할 수 있다.
    // 흐름 상태 보존은 best-effort — 실패해도 호출부(클릭 핸들러 등)를 중단시키지 않는다.
    // (in-memory ref 는 호출부가 별도로 보유하므로 SPA 내 동작은 유지되고, SSO 전체 리다이렉트
    //  복원만 불가해진다 — 깨지더라도 안전한 degrade.)
  }
}

/**
 * TTL 봉투에서 값을 읽는다. 만료·파싱 불가·부재 시 null 을 반환하고, 만료된 항목은 즉시 제거한다.
 * @param key sessionStorage 키.
 * @param ttlMs 유효 기간(ms). 저장 후 이 시간이 지나면 만료로 간주한다.
 * @param area 저장 영역(기본 session).
 * @returns 유효한 값, 없거나 만료면 null.
 */
export function getWithTtl<T>(key: string, ttlMs: number, area: StorageArea = 'session'): T | null {
  const store = storage(area);
  if (!store) return null; // SSR·스토리지 접근 불가 — 보존된 값 없음.

  // getItem/removeItem 호출도 차단 브라우저에서 throw 할 수 있어 전체를 try 로 감싼다(best-effort).
  try {
    const raw = store.getItem(key);
    if (raw == null) return null;

    let envelope: Envelope<T>;
    try {
      envelope = JSON.parse(raw) as Envelope<T>;
    } catch {
      // 손상된 값 — 제거하고 없는 것으로 취급.
      store.removeItem(key);
      return null;
    }

    // 구조 검증(외부에서 손댄 값 방어).
    if (typeof envelope?.savedAt !== 'number') {
      store.removeItem(key);
      return null;
    }

    // 만료 — 제거 후 null.
    if (Date.now() - envelope.savedAt > ttlMs) {
      store.removeItem(key);
      return null;
    }

    return envelope.value;
  } catch {
    return null; // 스토리지 접근 거부 — 값 없음으로 degrade.
  }
}

/** TTL 봉투 값을 제거한다(소비·로그아웃 시). 키가 없거나 스토리지 접근 불가여도 안전하다. */
export function removeWithTtl(key: string, area: StorageArea = 'session'): void {
  try {
    storage(area)?.removeItem(key);
  } catch {
    /* 스토리지 접근 거부 — 제거는 best-effort, 무시. */
  }
}
